import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { DocButton } from "@/components/ui/doc-button";
import { useFlows } from "@/lib/hooks/use-flows";
import { usePdfTemplates } from "@/lib/hooks/use-pdf-templates";
import { usePortfolioCollections } from "@/lib/hooks/use-portfolio-collections";
import { useRiskModels } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { PdfTemplateListItem } from "@/lib/types/pdf-template";
import { ClonePdfTemplateDialog } from "./clone-pdf-template-dialog";
import { PdfTemplateDialog } from "./pdf-template-dialog";
import { PdfTemplateFromTemplateDialog } from "./pdf-template-from-template-dialog";
import { T } from "./pdf-template-wrapper.i18n";
import { PdfTemplatesLoader } from "./pdf-templates-loader";
import { PdfTemplatesTable } from "./pdf-templates-table";

export function PdfTemplateWrapper() {
  const { tenantDefaultLanguage, tenantSupportedLanguages } = useUserLanguage();
  const t = useT(T);
  const { templates, isLoading } = usePdfTemplates();
  const { flows } = useFlows();
  const { collections } = usePortfolioCollections();
  // QL-212 — the full list, not just the active one: a flow may still reference
  // a deactivated model, and its scopes decide whether the template's risk block prints.
  const { models: riskModels } = useRiskModels();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [cloningTemplate, setCloningTemplate] = useState<PdfTemplateListItem | null>(null);

  // Map each collection to its requirement settings so the from-template dialog can
  // tell which flows can satisfy a template's governed components.
  const collectionSettingsById = new Map(
    collections.map((c) => [
      c.id,
      {
        requiresAssetAllocation: c.requiresAssetAllocation,
        requiresTimeseries: c.requiresTimeseries,
      },
    ]),
  );

  // QL-212 — each flow's risk-model scopes, so the from-template dialog can warn
  // when the template's risk section would print nothing for the chosen flow.
  const riskScopesByModelId = new Map(
    riskModels.map((m) => [
      m.id,
      {
        timeHorizonScope: m.timeHorizonScope,
        riskLevelScope: m.riskLevelScope,
        adjustmentQuestionsScope: m.adjustmentQuestionsScope,
      },
    ]),
  );

  // QL-334 — one template per flow, so a copy can only go to a flow without one.
  const flowsWithTemplate = new Set(templates.map((tpl) => tpl.flowId));

  const flowList = flows.map((f) => ({
    id: f.id,
    name: f.name,
    language: f.language,
    collectionSettings: f.portfolioCollectionId
      ? (collectionSettingsById.get(f.portfolioCollectionId) ?? null)
      : null,
    riskScopes: f.riskModelId ? (riskScopesByModelId.get(f.riskModelId) ?? null) : null,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.pdfTemplates }]}
        title={t.pdfTemplates}
        description={t.pageDescription}
        actions={
          <DocButton docSlug="pdf-templates" variant="outline" size="sm" className="gap-2">
            {t.documentation}
          </DocButton>
        }
      />

      <div className="flex justify-end gap-2 mb-4">
        <Button variant="outline" onClick={() => setTemplateDialogOpen(true)}>
          {t.createTemplate}
        </Button>
        <Button onClick={() => setDialogOpen(true)}>{t.createBlank}</Button>
      </div>

      {isLoading ? (
        <PdfTemplatesLoader />
      ) : (
        <PdfTemplatesTable templates={templates} onClone={setCloningTemplate} />
      )}

      <ClonePdfTemplateDialog
        open={!!cloningTemplate}
        template={cloningTemplate}
        availableFlows={flowList.filter((f) => !flowsWithTemplate.has(f.id))}
        onClose={() => setCloningTemplate(null)}
      />

      <PdfTemplateDialog
        open={dialogOpen}
        template={null}
        flows={flowList}
        supportedLanguages={tenantSupportedLanguages}
        defaultLanguage={tenantDefaultLanguage}
        onClose={() => setDialogOpen(false)}
      />

      <PdfTemplateFromTemplateDialog
        open={templateDialogOpen}
        flows={flowList}
        supportedLanguages={tenantSupportedLanguages}
        defaultLanguage={tenantDefaultLanguage}
        onClose={() => setTemplateDialogOpen(false)}
      />
    </div>
  );
}
